// Bouton « appuyer pour parler » (MediaRecorder) — utilisé par le répartiteur et le chauffeur.
import { useEffect, useRef, useState } from "react";
import { Mic, Loader2, WifiOff, AlertTriangle, ExternalLink } from "lucide-react";
import { sendOrQueue } from "@/lib/pttQueue";
import { toast } from "sonner";

const MAX_MS = 60000;
const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];

const pickMime = () => MIME_CANDIDATES.find(
  (m) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) || "";

const inIframe = () => { try { return window.self !== window.top; } catch { return true; } };

/** Message précis selon la raison réelle du refus du micro. */
function micProblem(err) {
  if (!window.isSecureContext) {
    return "Le micro exige une connexion sécurisée (https).";
  }
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
    return "Ce navigateur ne permet pas l'enregistrement audio. Utilisez Chrome, Edge ou Safari à jour.";
  }
  const name = err?.name || "";
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return "Aucun micro détecté sur cet appareil.";
  }
  if (name === "NotReadableError" || name === "AbortError") {
    return "Le micro est déjà utilisé par une autre application. Fermez-la puis réessayez.";
  }
  if (inIframe()) {
    return "Le micro est bloqué dans l'aperçu intégré. Ouvrez l'application dans un onglet séparé, puis autorisez le micro.";
  }
  return "Accès au micro refusé. Touchez l'icône de cadenas à gauche de l'adresse, mettez « Microphone » à « Autoriser », puis rechargez la page.";
}

export default function PTTButton({ channel = "driver", driverId = null, disabled = false,
                                    onSent, variant = "dispatch", label = "PARLER" }) {
  const [state, setState] = useState("idle");   // idle | recording | sending
  const [elapsed, setElapsed] = useState(0);
  const [micError, setMicError] = useState(null);
  const rec = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);
  const startedAt = useRef(0);
  const timer = useRef(null);
  const autoStop = useRef(null);

  useEffect(() => () => {
    clearInterval(timer.current); clearTimeout(autoStop.current);
    if (stream.current) stream.current.getTracks().forEach((t) => t.stop());
  }, []);

  const start = async () => {
    if (state !== "idle" || disabled) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined"
        || !window.isSecureContext) {
      return setMicError(micProblem());
    }
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      setMicError(null);
    } catch (err) {
      return setMicError(micProblem(err));
    }
    const mimeType = pickMime();
    chunks.current = [];
    rec.current = new MediaRecorder(stream.current, mimeType ? { mimeType, audioBitsPerSecond: 32000 } : undefined);
    rec.current.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
    rec.current.onstop = finish;
    rec.current.start();
    startedAt.current = Date.now();
    setElapsed(0);
    setState("recording");
    if (navigator.vibrate) navigator.vibrate(30);
    timer.current = setInterval(() => setElapsed(Math.round((Date.now() - startedAt.current) / 1000)), 250);
    autoStop.current = setTimeout(() => stop(), MAX_MS);
  };

  const stop = () => {
    clearInterval(timer.current); clearTimeout(autoStop.current);
    if (rec.current && rec.current.state !== "inactive") rec.current.stop();
  };

  const finish = async () => {
    const duration = (Date.now() - startedAt.current) / 1000;
    if (stream.current) { stream.current.getTracks().forEach((t) => t.stop()); stream.current = null; }
    const blob = new Blob(chunks.current, { type: rec.current?.mimeType || "audio/webm" });
    if (duration < 0.6 || !blob.size) { setState("idle"); return toast.message("Trop court — maintenez le bouton"); }
    setState("sending");
    const ext = (blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm");
    try {
      const res = await sendOrQueue({ blob, ext, duration_sec: duration, channel, driver_id: driverId });
      if (res.queued) toast.message("Hors réseau — message vocal en attente d'envoi");
      else toast.success(`Message vocal envoyé (${Math.round(duration)} s)`);
      if (onSent) onSent(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally { setState("idle"); }
  };

  const recording = state === "recording";
  const driverStyle = variant === "driver";
  const base = driverStyle
    ? "w-full h-24 rounded-2xl font-display text-2xl font-extrabold"
    : "w-full h-14 rounded-xl font-display text-base font-bold";

  return (
    <div className="space-y-2">
      {micError && (
        <div className={`rounded-lg px-3 py-2 text-xs leading-snug border ${
          driverStyle ? "bg-amber-500/15 border-amber-400/40 text-amber-200"
                      : "bg-amber-50 border-amber-200 text-amber-800"}`}
          data-testid="ptt-mic-error">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
            <span>{micError}</span>
          </div>
          {inIframe() && (
            <a href={window.location.href} target="_blank" rel="noreferrer"
              data-testid="ptt-open-new-tab"
              className="mt-2 inline-flex items-center gap-1.5 font-bold underline">
              <ExternalLink className="w-3.5 h-3.5" /> Ouvrir dans un onglet séparé
            </a>
          )}
        </div>
      )}
      <button
        data-testid={`ptt-button-${variant}`}
        disabled={disabled || state === "sending"}
        onPointerDown={(e) => { e.preventDefault(); start(); }}
        onPointerUp={(e) => { e.preventDefault(); stop(); }}
        onPointerLeave={() => recording && stop()}
        onContextMenu={(e) => e.preventDefault()}
        className={`${base} select-none touch-none flex items-center justify-center gap-3 transition-all disabled:opacity-50
          ${recording
            ? "bg-red-600 text-white scale-[0.98] shadow-[0_0_0_6px_rgba(220,38,38,0.25)] animate-pulse"
            : driverStyle ? "bg-yellow-400 text-slate-900 active:scale-[0.98]" : "bg-primary text-primary-foreground active:scale-[0.98]"}`}
      >
        {state === "sending" ? <Loader2 className="w-7 h-7 animate-spin" />
          : <Mic className={driverStyle ? "w-9 h-9" : "w-5 h-5"} />}
        {state === "sending" ? "ENVOI…"
          : recording ? `${elapsed}s — RELÂCHER POUR ENVOYER`
          : disabled ? <span className="flex items-center gap-2"><WifiOff className="w-4 h-4" /> {label}</span>
          : label}
      </button>
    </div>
  );
}
